'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import Image from 'next/image'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  FileIcon,
  MoreHorizontalIcon,
  CalendarCheckIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  ImageIcon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DeleteConfirmationDialog, EventsFilters, LoadingState, SortableHeader } from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import {
  formatDateTime,
  getCountryCodeFromLocale,
  getLocalizedValue,
  getResizedPictureUrl,
  textLimit
} from '@/lib/utils'
import { useEvents, useDeleteEvent, useBulkDeleteEvents } from '@/hooks'
import { SortEnum } from '@/enums'
import { EventsFilterParams } from '@/types/event'
import { useEventsStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // Event selection hooks from Zustand store
  const selectedEventIds = useEventsStore(state => state.selectedEventIds)
  const toggleEventSelection = useEventsStore(state => state.toggleEventSelection)
  const selectAllEvents = useEventsStore(state => state.selectAllEvents)
  const deselectAllEvents = useEventsStore(state => state.deselectAllEvents)
  const isEventSelected = useEventsStore(state => state.isEventSelected)

  const { metaState } = useMetaStore()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'EVENTS', 'CREATE')
  const canShow = hasPermission(currentUser, 'EVENTS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'EVENTS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'EVENTS', 'DELETE')
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { mutate: deleteEventMutate, isPending: isDeleting } = useDeleteEvent()
  const { mutate: bulkDeleteEventsMutate, isPending: isBulkDeleting } = useBulkDeleteEvents()

  const filters = useMemo<EventsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      isBlocked: params.get('isBlocked') === 'true' ? true : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { data: eventsData, isLoading, error, refetch, isFetching } = useEvents(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<EventsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<EventsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/events')
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleSort = (sortBy: string) => {
    const newSort = filters.sortBy === sortBy && filters.sort === SortEnum.ASC ? SortEnum.DESC : SortEnum.ASC
    handleFiltersChange({ sortBy, sort: newSort })
  }

  const handleView = (id: string) => {
    router.push(`/events/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/events/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteEventId(id)
  }

  const confirmDeleteEvent = () => {
    if (deleteEventId) {
      deleteEventMutate(deleteEventId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['events'] })
          toast.success(t('deleted_successfully'))
          setDeleteEventId(null)
        },
        onError: () => {
          toast.error(t('error_deleting'))
        }
      })
    }
  }

  const confirmBulkDeleteEvents = () => {
    if (selectedEventIds.length > 0) {
      bulkDeleteEventsMutate(selectedEventIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['events'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }
        }
      })
    }
  }

  const renderActionItems = (eventId: string, inDropdown = true) => {
    const items = [
      ...(canShow
        ? [
            {
              label: t('event_details'),
              icon: EyeIcon,
              onClick: () => handleView(eventId)
            }
          ]
        : []),
      ...(canUpdate
        ? [
            {
              label: t('edit_event'),
              icon: EditIcon,
              onClick: () => handleEdit(eventId)
            }
          ]
        : []),
      ...(canDelete
        ? [
            {
              label: t('delete'),
              icon: Trash2Icon,
              onClick: () => handleDelete(eventId),
              className: 'text-red-600 hover:text-red-700'
            }
          ]
        : [])
    ]

    if (inDropdown) {
      return items.map(item => (
        <DropdownMenuItem
          key={item.label}
          className={item.className}
          onSelect={e => {
            e.preventDefault()
            e.stopPropagation()
            item.onClick()
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            item.onClick()
          }}>
          <item.icon className="mr-2 h-4 w-4" />
          {item.label}
        </DropdownMenuItem>
      ))
    }

    return items.map(item => (
      <ContextMenuItem
        key={item.label}
        className={item.className}
        onSelect={e => {
          e.preventDefault()
          e.stopPropagation()
          item.onClick()
        }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          item.onClick()
        }}>
        <item.icon className="mr-2 h-4 w-4" />
        {item.label}
      </ContextMenuItem>
    ))
  }

  const handleBulkDelete = () => {
    setBulkDeleteConfirmOpen(true)
  }

  const handleSelectAll = () => {
    if (eventsData?.items) {
      if (selectedEventIds.length === eventsData.items.length) {
        deselectAllEvents()
      } else {
        selectAllEvents(eventsData.items)
      }
    }
  }

  if (isLoading) {
    return <LoadingState isLoading={true}>Loading events...</LoadingState>
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('error')}</CardTitle>
          <CardDescription>{t('error_loading_record')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()}>{t('try_again')}</Button>
        </CardContent>
      </Card>
    )
  }

  if (!eventsData) {
    return null
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheckIcon className="h-5 w-5" />
                {t('events')}
              </CardTitle>
              <CardDescription>{t('events_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/events/create">
                    <SquarePlusIcon />
                    <span className="hidden lg:flex">{t('create_event')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedEventIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedEventIds.length})</span>
                  </div>
                </Button>
              )}
              <EventsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error as any} onRetry={refetch} loadingRows={5}>
            {eventsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          {canDelete && (
                            <Checkbox
                              checked={
                                eventsData.items.length > 0 && selectedEventIds.length === eventsData.items.length
                              }
                              onCheckedChange={handleSelectAll}
                              className="cursor-pointer"
                            />
                          )}
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t('picture')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy={`translations.${locale}.title`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('title')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="beginDate"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('start_date')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="endDate"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('end_date')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        eventsData.items.map((event, index) => (
                          <ContextMenu key={event.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(event.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isEventSelected(event.id)}
                                        onCheckedChange={() => toggleEventSelection(event.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {event.mainPicture ? (
                                    <Image
                                      src={getResizedPictureUrl(event.mainPicture.path, 90, 90)}
                                      alt={event.mainPicture.name || ''}
                                      width={90}
                                      height={90}
                                      className="mx-auto max-h-22.5 w-auto border object-contain"
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <FileIcon className="text-muted-foreground h-10 w-10" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>
                                      {textLimit(getLocalizedValue(event, 'title', locale, defaultLocale), 120)}
                                    </span>
                                    <span className="text-muted-foreground text-xs">ID: {event.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(event.beginDate, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(event.endDate, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(event.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(event.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(event.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(event.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={eventsData.page}
                  totalPages={eventsData.pages}
                  totalItems={eventsData.total}
                  pageSize={eventsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteEventId}
          onCancel={() => setDeleteEventId(null)}
          onConfirm={confirmDeleteEvent}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteEvents}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
